/**
 * Navigation controller.
 *
 * The orchestrator that ties every piece of the engine together.
 * Event listeners (click, hover, form, popstate) and the public
 * Drupal.sdxNavigation API both delegate here. Every collaborator
 * is passed in via constructor injection so the controller does
 * not reach out to module-level globals and can be reasoned about
 * as a self-contained unit.
 *
 * Key responsibilities:
 *
 * - Aborts any in-flight fetch and starts a new one on each
 *   navigation request.
 * - Computes DataProvider stale prop keys from URL param diffs so
 *   content-scope fetches can pass them via the X-SDX-Stale header.
 * - Consults the prefetch cache first: reuses a resolved result,
 *   adopts a still-pending promise, or starts fresh on rejection.
 * - Pushes or replaces a history entry before the fetch so the
 *   address bar reflects the target immediately.
 * - Dispatches the legacy sdx:navigate-* DOM events alongside the
 *   state-store updates for backward compatibility with consumers
 *   that listen on the DOM bus.
 * - Returns a Promise that resolves when the navigation settles,
 *   so callers can `await Drupal.sdxNavigation.navigate(url)`.
 */

import { classifyScope, toHistoryUrl } from './url';
import { requiresDocumentNavigation } from './document-policy';
import { toFetchableUrl } from '@sdx/core/transport';
import { fetchPage, normalizeResponse } from './fetch-page';
import type { FetchPageContext } from './fetch-page';
import { processHtmlResponse, processPartialResponse } from './response-processor';
import type { ResponseProcessorContext } from './response-processor';
import { logger } from './logger';
import type {
  NavScope,
  NavState,
  NavigateOptions,
  PageData,
  FetchResult,
} from './types';
import { NAV_EVENT } from './types';
import type { NavStateStore } from './nav-state-store';
import type { PrefetchCache, PrefetchEntry } from './prefetch-cache';
import type { TagStore } from './tag-store';
import type { RegionChecksums } from './region-checksums';
import type { AssetInventory } from './asset-inventory';
import type { ComponentResolverRegistry } from './component-resolver';
import type { ProgressBar } from './progress-bar';

export interface NavigationControllerOptions {
  readonly state: NavStateStore;
  readonly prefetchCache: PrefetchCache;
  readonly tagStore: TagStore;
  readonly regionChecksums: RegionChecksums;
  readonly inventory: AssetInventory;
  readonly componentResolvers: ComponentResolverRegistry;
  readonly progressBar: ProgressBar;
  readonly contentSelector: string;
  readonly devMode: boolean;
}

export class NavigationController {
  private readonly state: NavStateStore;
  private readonly prefetchCache: PrefetchCache;
  private readonly tagStore: TagStore;
  private readonly regionChecksums: RegionChecksums;
  private readonly inventory: AssetInventory;
  private readonly componentResolvers: ComponentResolverRegistry;
  private readonly progressBar: ProgressBar;
  private readonly contentSelector: string;
  private readonly devMode: boolean;

  private menuHash = '';
  private routeName = '';
  private currentAbort: AbortController | null = null;
  private navigationId = 0;

  constructor(options: NavigationControllerOptions) {
    this.state = options.state;
    this.prefetchCache = options.prefetchCache;
    this.tagStore = options.tagStore;
    this.regionChecksums = options.regionChecksums;
    this.inventory = options.inventory;
    this.componentResolvers = options.componentResolvers;
    this.progressBar = options.progressBar;
    this.contentSelector = options.contentSelector;
    this.devMode = options.devMode;
  }

  /** Starts a navigation. Returns a Promise that resolves with the
   *  final nav state once the response has been processed. */
  async start(
    url: string,
    options: NavigateOptions = {},
  ): Promise<Readonly<NavState>> {
    // Sanitize before anything downstream sees the URL: fetch()
    // rejects credentialed URLs outright, and beginNavigation would
    // otherwise write the credentialed form into history, re-seeding
    // it into every later popstate.
    url = toFetchableUrl(url);
    if (this.navigateDocument(url, options.replace ?? false)) return this.state.getState();
    const scope = options.scope ?? classifyScope(url, this.state.getState().url);
    const staleKeys = this.computeStaleKeys(url, scope);
    const thisNavId = this.beginNavigation(url, options, scope, staleKeys);

    if (scope !== 'content') {
      this.progressBar.start();
    }

    try {
      const result = await this.fetchOrAdopt(url, scope, staleKeys);
      return await this.completeNavigation(result, url, thisNavId, scope);
    }
    catch (error) {
      return this.handleNavigationError(error, url, thisNavId);
    }
  }

  /** Adopts an already-dispatched Response (e.g. the response to a
   *  form POST intercepted by `installFormListener`) as the result
   *  of a navigation to `finalUrl`. Skips the duplicate fetch that
   *  was previously discarding the POST body and consuming the
   *  Drupal messenger flash before the user ever saw it.
   *
   *  The caller is expected to have followed redirects
   *  (`redirect: 'follow'`) and to have included the
   *  `X-SDX-Navigate` header on the request so the server's
   *  SPA pipeline wraps the response the same way it would a
   *  fresh SPA fetch. */
  async adoptResponse(
    response: Response,
    finalUrl: string,
    options: NavigateOptions = {},
  ): Promise<Readonly<NavState>> {
    finalUrl = toFetchableUrl(finalUrl);
    if (this.navigateDocument(finalUrl, options.replace ?? false)) return this.state.getState();
    const scope = options.scope ?? classifyScope(finalUrl, this.state.getState().url);
    const thisNavId = this.beginNavigation(finalUrl, options, scope, []);

    try {
      const result = await normalizeResponse(response, finalUrl);
      return await this.completeNavigation(result, finalUrl, thisNavId, scope);
    }
    catch (error) {
      return this.handleNavigationError(error, finalUrl, thisNavId);
    }
  }

  /**
   * Shared setup for every navigation entry point.
   *
   * Aborts any in-flight fetch, claims a fresh navigation id,
   * publishes the loading state, dispatches the start event, and
   * writes the history entry. Returns the navigation id so the
   * caller can detect stale completions in `completeNavigation`.
   */
  private beginNavigation(
    url: string,
    options: NavigateOptions,
    scope: NavScope,
    staleKeys: string[],
  ): number {
    const pushState = options.pushState ?? !options.replace;

    this.abortInFlight();
    this.currentAbort = new AbortController();
    const thisNavId = ++this.navigationId;

    const path = this.extractPath(url);
    this.state.setLoading(url, path, scope);
    this.dispatchStart(path, url, thisNavId, scope, staleKeys);

    // `url` is credential-stripped for fetch(); the history write
    // must re-apply the document's own userinfo or the spec's
    // same-userinfo check makes pushState throw a SecurityError on
    // documents opened through a credentialed URL.
    if (options.replace) {
      history.replaceState({ sdxNav: true, id: thisNavId }, '', toHistoryUrl(url));
    }
    else if (pushState) {
      history.pushState({ sdxNav: true, id: thisNavId }, '', toHistoryUrl(url));
    }

    return thisNavId;
  }

  /**
   * Applies a fetched result to the state store if the navigation
   * is still the most recent one. Stale completions (superseded by
   * a newer `start()` / `adoptResponse()` call) are dropped without
   * touching the UI so rapid navigation sequences never surface
   * out-of-order content.
   */
  private async completeNavigation(
    result: FetchResult,
    url: string,
    thisNavId: number,
    scope: NavScope,
  ): Promise<Readonly<NavState>> {
    if (thisNavId !== this.navigationId) {
      return this.state.getState();
    }
    if (result.type !== 'batch' && result.finalUrl && this.navigateDocument(result.finalUrl, true)) {
      return this.state.getState();
    }
    url = this.adoptRedirectTarget(result, url, thisNavId);
    await this.applyResult(result, url, thisNavId, scope);
    return this.state.getState();
  }

  /**
   * Rewrites the navigation onto the URL a followed redirect landed
   * on, so the address bar, history entry and nav state carry the
   * canonical URL (what a full page load would show) instead of the
   * URL that was clicked. Cross-origin redirects are left alone -
   * the swap already happened against same-origin assumptions and a
   * cross-origin location must not be written into history.
   */
  private adoptRedirectTarget(
    result: FetchResult,
    url: string,
    thisNavId: number,
  ): string {
    if (result.type === 'batch' || !result.finalUrl) {
      return url;
    }
    let resolved: URL;
    try {
      resolved = new URL(result.finalUrl, location.origin);
    }
    catch {
      return url;
    }
    if (resolved.origin !== location.origin) {
      return url;
    }
    const finalUrl = toFetchableUrl(resolved.href);
    if (finalUrl === url) {
      return url;
    }
    history.replaceState({ sdxNav: true, id: thisNavId }, '', toHistoryUrl(finalUrl));
    return finalUrl;
  }

  private handleNavigationError(
    error: unknown,
    url: string,
    thisNavId: number,
  ): Readonly<NavState> {
    if (isAbortError(error)) {
      return this.state.getState();
    }
    this.failWithFallback(url, thisNavId, error);
    return this.state.getState();
  }

  /** Leaves this JS runtime before exposing a protected destination to history. */
  private navigateDocument(url: string, replace: boolean): boolean {
    if (!requiresDocumentNavigation(url)) return false;
    this.abortInFlight();
    if (replace) location.replace(toHistoryUrl(url));
    else location.assign(toHistoryUrl(url));
    return true;
  }

  prefetch(url: string): void {
    // Same sanitization as start() so the cache key a later click
    // looks up matches the key the prefetch was stored under.
    url = toFetchableUrl(url);
    if (requiresDocumentNavigation(url)) return;
    if (this.prefetchCache.has(url)) return;
    if (this.isNavigatingTo(url)) return;
    const scope = classifyScope(url);
    this.prefetchCache.prefetch(url, scope);
  }

  /**
   * True when `url` is the navigation currently in flight or the
   * page already committed. Prefetching either is a wasted server
   * render: the in-flight fetch will resolve first, and the
   * committed page is what the user is looking at.
   */
  isNavigatingTo(url: string): boolean {
    return this.state.getState().url === toFetchableUrl(url);
  }

  abort(): void {
    this.abortInFlight();
  }

  captureInitialState(): void {
    history.replaceState({ sdxNav: true }, '');
    this.regionChecksums.captureFromInitialPage();
  }

  private abortInFlight(): void {
    if (this.currentAbort) {
      this.currentAbort.abort();
      this.currentAbort = null;
    }
  }

  private computeStaleKeys(url: string, scope: NavScope): string[] {
    if (scope === 'content' && this.tagStore.hasComponent()) {
      // Compare against the last committed URL from the state
      // store rather than `location.href`. On popstate the
      // browser has already moved location to the target, so a
      // location-based diff would compare the URL to itself and
      // return no stale keys.
      const sourceUrl = this.state.getState().url || location.href;
      return this.tagStore.getStaleKeys(sourceUrl, url);
    }
    // Clear stale state for any non-content navigation; leaking
    // stale checksums from the previous route into the next
    // request would cause the server to skip props it thinks are
    // unchanged but actually aren't.
    this.tagStore.clear();
    return [];
  }

  private async fetchOrAdopt(
    url: string,
    scope: NavScope,
    staleKeys: string[],
  ): Promise<FetchResult> {
    // Partial reloads driven by stale-key detection must start a
    // fresh fetch - the stale key set is specific to the current
    // state, so adopting a prefetch started with a different set
    // would produce the wrong server response.
    if (staleKeys.length > 0) {
      return this.fetchFresh(url, scope, staleKeys);
    }

    const cached = this.prefetchCache.take(url);
    if (!cached) {
      return this.fetchFresh(url, scope, staleKeys);
    }
    return this.useCachedEntry(cached, url, scope, staleKeys);
  }

  private fetchFresh(
    url: string,
    scope: NavScope,
    staleKeys: string[],
  ): Promise<FetchResult> {
    const signal = this.currentAbort!.signal;
    return fetchPage(url, signal, scope, staleKeys, this.fetchContext());
  }

  private async useCachedEntry(
    entry: PrefetchEntry,
    url: string,
    scope: NavScope,
    staleKeys: string[],
  ): Promise<FetchResult> {
    if (entry.status === 'resolved' && entry.result) {
      return entry.result;
    }
    if (entry.status === 'pending') {
      try {
        return await entry.promise;
      }
      catch {
        return this.fetchFresh(url, scope, staleKeys);
      }
    }
    return this.fetchFresh(url, scope, staleKeys);
  }

  private async applyResult(
    result: FetchResult,
    url: string,
    thisNavId: number,
    scope: NavScope,
  ): Promise<void> {
    const ctx: ResponseProcessorContext = {
      contentSelector: this.contentSelector,
      devMode: this.devMode,
      inventory: this.inventory,
      componentResolvers: this.componentResolvers,
      tagStore: this.tagStore,
      regionChecksums: this.regionChecksums,
      menuHash: this.menuHash,
      routeName: this.routeName,
    };

    // Batch redirect: launch the batch modal in-place and
    // SPA-navigate to the destination when it completes. Restore
    // the address bar to the pre-navigation URL so the user stays
    // on the originating page while the batch runs.
    if (result.type === 'batch') {
      this.progressBar.complete();
      this.currentAbort = null;

      // Restore the pre-navigation state so the page behind the
      // modal shows the original content, not a skeleton loader.
      const prevState = this.state.getState();
      const prevUrl = prevState.url || '/';
      const prevPath = prevState.path || '/';
      history.replaceState({ sdxNav: true }, '', toHistoryUrl(prevUrl));
      if (prevState.pageData) {
        this.state.setIdle(prevState.pageData, prevUrl, prevPath);
      }

      const { BatchModal } = await import('./batch-modal');
      new BatchModal().run(result.batchUrl, result.returnUrl, (dest) => {
        void this.start(dest);
      });
      return;
    }

    const outcome = result.type === 'partial'
      ? await processPartialResponse(result.data, url, scope, ctx)
      : await processHtmlResponse(result.data, url, ctx);

    // Sync mutable fields back. The processor may have updated
    // menuHash or routeName from envelope fields, so we read the
    // context after it runs.
    this.menuHash = ctx.menuHash;
    this.routeName = ctx.routeName;

    if (outcome.kind === 'full-reload') {
      window.location.replace(url);
      return;
    }
    if (outcome.kind === 'handled-out-of-band') {
      this.progressBar.complete();
      this.currentAbort = null;
      return;
    }

    this.finishNavigation(outcome.pageData, url, thisNavId);
  }

  private finishNavigation(pageData: PageData, url: string, thisNavId: number): void {
    pageData.id = thisNavId;
    const path = this.extractPath(url);
    this.state.setIdle(pageData, url, path);
    document.dispatchEvent(
      new CustomEvent(NAV_EVENT.END, { detail: pageData }),
    );
    this.progressBar.complete();
    this.currentAbort = null;
  }

  private failWithFallback(url: string, thisNavId: number, error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);
    logger.warn(`Navigation failed; falling back to full load: ${message}`);
    this.progressBar.complete();
    this.currentAbort = null;
    this.state.setError(message);
    document.dispatchEvent(
      new CustomEvent(NAV_EVENT.ERROR, {
        detail: { path: url, error: message, id: thisNavId },
      }),
    );
    window.location.href = url;
  }

  private dispatchStart(
    path: string,
    url: string,
    id: number,
    scope: NavScope,
    staleKeys: string[],
  ): void {
    document.dispatchEvent(
      new CustomEvent(NAV_EVENT.START, {
        detail: { path, url, id, scope, staleKeys },
      }),
    );
  }

  private extractPath(url: string): string {
    try {
      return new URL(url, location.origin).pathname;
    }
    catch {
      return url;
    }
  }

  private fetchContext(): FetchPageContext {
    return {
      tagStore: this.tagStore,
      regionChecksums: this.regionChecksums,
      menuHash: this.menuHash,
      routeName: this.routeName,
    };
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}
